import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Outlet, useLocation, matchPath } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import BottomNav from './Navigation/BottomNav';
import SideNav from './Navigation/SideNav';
import SideDrawer from './Navigation/SideDrawer';
import RightSidebar from './RightSidebar/RightSidebar';

const useSidebarStickyTop = () => {
  const sidebarRef = useRef(null);

  const recalc = useCallback(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const scrollContainer = sidebar.closest('[data-app-scroll-container]');
    if (!scrollContainer) return;

    const viewportH = scrollContainer.clientHeight;
    const sidebarH = sidebar.offsetHeight;

    sidebar.style.top =
      sidebarH <= viewportH ? '0px' : `${viewportH - sidebarH}px`;
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;

    const observer = new ResizeObserver(recalc);
    observer.observe(sidebar);
    recalc();

    window.addEventListener('resize', recalc);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalc);
    };
  }, [recalc]);

  return sidebarRef;
};

const Layout = () => {
  const [openSideDrawer, setOpenSideDrawer] = useState(false);
  const location = useLocation();

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    setOpenSideDrawer(false);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const hideSidebar =
    matchPath('/settings/*', location.pathname) ||
    matchPath('/apoiador/*', location.pathname) ||
    matchPath('/assinaturas/*', location.pathname) ||
    matchPath('/messages', location.pathname) ||
    matchPath('/messages/*', location.pathname);
  const isSearchPage = matchPath('/search', location.pathname);
  const excludeWidgets = useMemo(
    () => (isSearchPage ? ['search'] : []),
    [isSearchPage]
  );

  const sidebarRef = useSidebarStickyTop();

  return (
    <div
      ref={scrollContainerRef}
      className="h-[100vh] overflow-y-auto"
      data-app-scroll-container="true"
    >
      <div className="relative flex min-h-full">
        <div className="sm:hidden">
          <SideDrawer
            isOpen={openSideDrawer}
            direction="left"
            onDismiss={() => setOpenSideDrawer(false)}
          >
            <SideNav onNavigate={() => setOpenSideDrawer(false)} />
          </SideDrawer>
        </div>
        <div className="hidden sm:block sm:w-auto lg:w-64 sticky top-0 h-screen">
          <SideNav />
        </div>
        <div className="flex-1 min-w-0">
          <Outlet context={[openSideDrawer, setOpenSideDrawer]} />
        </div>
        {!hideSidebar && (
          <div className="hidden lg:block lg:w-80 xl:w-96 shrink-0">
            <div ref={sidebarRef} className="sticky px-3">
              <RightSidebar excludeWidgets={excludeWidgets} />
            </div>
          </div>
        )}
      </div>
      <Toaster
        position="bottom-center"
        containerStyle={{
          marginBottom: '3.5rem',
          inset: '0',
        }}
        toastOptions={{
          duration: 2000,
        }}
      />
      <div className="sm:hidden">
        <div className="fixed bottom-0 left-0 w-full">
          <BottomNav />
        </div>
      </div>
    </div>
  );
};

export default Layout;
